import { DatePicker } from "@/common/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Lot } from "@/features/lots/types";
import { X } from "lucide-react";
import type { CostCategory } from "../utils/categories";
import { isoLocalDate } from "../utils/formatPeriod";
import { CategorySelect } from "./CategorySelect";

export type Filters = {
  lotId: string; // "" = todos
  category: CostCategory | "";
  periodStart: string; // YYYY-MM-DD ó ""
  periodEnd: string;
};

type Props = {
  filters: Filters;
  onChange: (next: Filters) => void;
  lots: Lot[];
};

const ALL_LOTS = "__all__";

export function CostAllocationsFilters({ filters, onChange, lots }: Props) {
  const periodStartDate = filters.periodStart ? parseDate(filters.periodStart) : undefined;
  const periodEndDate = filters.periodEnd ? parseDate(filters.periodEnd) : undefined;

  const hasAny =
    filters.lotId !== "" ||
    filters.category !== "" ||
    filters.periodStart !== "" ||
    filters.periodEnd !== "";

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-app-border bg-app-surface p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-[13px] text-app-ink-2">Filtros</h2>
        {hasAny && (
          <button
            type="button"
            onClick={() => onChange({ lotId: "", category: "", periodStart: "", periodEnd: "" })}
            className="inline-flex items-center gap-1 text-[12px] text-app-ink-3 hover:text-app-ink-1"
          >
            <X size={12} />
            Limpiar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-lot" className="font-medium text-[12px] text-app-ink-3">
            Lote
          </label>
          <Select
            value={filters.lotId === "" ? ALL_LOTS : filters.lotId}
            onValueChange={(v) => onChange({ ...filters, lotId: v === ALL_LOTS ? "" : v })}
          >
            <SelectTrigger id="filter-lot" className="h-10 w-full text-app-ink-1">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_LOTS}>Todos</SelectItem>
              {lots.map((lot) => (
                <SelectItem key={lot.id} value={lot.id}>
                  {lot.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-category" className="font-medium text-[12px] text-app-ink-3">
            Categoría
          </label>
          <CategorySelect
            id="filter-category"
            value={filters.category}
            onChange={(c) => onChange({ ...filters, category: c })}
            placeholder="Todas"
            allowClear
            aria-label="Categoría"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-from" className="font-medium text-[12px] text-app-ink-3">
            Desde
          </label>
          <DatePicker
            id="filter-from"
            value={periodStartDate}
            onChange={(d) => onChange({ ...filters, periodStart: d ? isoLocalDate(d) : "" })}
            maxDate={periodEndDate}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-to" className="font-medium text-[12px] text-app-ink-3">
            Hasta
          </label>
          <DatePicker
            id="filter-to"
            value={periodEndDate}
            onChange={(d) => onChange({ ...filters, periodEnd: d ? isoLocalDate(d) : "" })}
            minDate={periodStartDate}
          />
        </div>
      </div>
    </div>
  );
}

function parseDate(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m?.[1] || !m[2] || !m[3]) return undefined;
  const y = Number.parseInt(m[1], 10);
  const month = Number.parseInt(m[2], 10);
  const day = Number.parseInt(m[3], 10);
  // Post-validar: el regex acepta 2026-99-99 pero `new Date(2026, 98, 99)`
  // produce un Date overflowed (~feb 2034). Confirmamos que los componentes
  // del Date construido coinciden con los del input antes de devolverlo.
  const d = new Date(y, month - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return undefined;
  }
  return d;
}
